import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AccentButton } from '../components/Button';
import ThemeSwitch from '../components/ThemeSwitch';
import Folder from '../components/Folder';
import ItemCard from '../components/ItemCard';
import Input from '../components/Input';
import { CopyIcon, TrashIcon } from '../icons';
import { useAuthStore } from '../../stores/authStore';

const DEMO_ACTIONS = [
  { key: 'copy', icon: CopyIcon, onClick: () => {} },
  { key: 'delete', icon: TrashIcon, onClick: () => {} },
];

// ─── Demo text item ──────────────────────────────────────────────────────────
const DemoTextItem = ({ content, device = 'apple' }) => (
  <ItemCard date="5 min ago" device={device} actions={DEMO_ACTIONS} className="max-w-none w-full">
    <div className="p-2 h-full flex items-start">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-[#121212] dark:text-[#D0D0D0] whitespace-pre overflow-hidden">
          {content}
        </div>
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-[#FFFFFF] dark:from-[#121212] to-transparent pointer-events-none" />
    <div className="absolute top-0 right-0 bottom-0 w-5 bg-gradient-to-l from-[#FFFFFF] dark:from-[#121212] to-transparent pointer-events-none" />
  </ItemCard>
);

// ─── Demo image item — uses a real placeholder image ────────────────────────
const DemoImageItem = ({ device = 'android' }) => (
  <ItemCard date="5 min ago" device={device} actions={DEMO_ACTIONS} className="max-w-none w-full">
    <div className="w-full h-full">
      <img
        src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=340&h=100&fit=crop&auto=format"
        alt="Clipboard screenshot"
        className="w-full h-full object-cover rounded-2xl"
      />
    </div>
  </ItemCard>
);

// ─── Feature card ────────────────────────────────────────────────────────────
const Feature = ({ icon, title, description }) => (
  <div className="flex flex-col gap-3 p-6 rounded-3xl bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020]">
    <div className="w-10 h-10 rounded-xl bg-[#F1F1F1] dark:bg-[#2A2A2A] flex items-center justify-center text-xl">
      {icon}
    </div>
    <h3 className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0]">{title}</h3>
    <p className="text-sm text-[#7B7B7B] dark:text-[#848484] leading-relaxed">{description}</p>
  </div>
);

// ─── How it works step ───────────────────────────────────────────────────────
const Step = ({ number, title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="w-8 h-8 rounded-full bg-[#121212] dark:bg-[#D0D0D0] flex items-center justify-center shrink-0 mt-0.5">
      <span className="text-xs font-bold text-white dark:text-[#121212]">{number}</span>
    </div>
    <div>
      <h3 className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0] mb-1">{title}</h3>
      <p className="text-sm text-[#7B7B7B] dark:text-[#848484] leading-relaxed">{description}</p>
    </div>
  </div>
);

// ─── Waitlist form ───────────────────────────────────────────────────────────
const WaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    // TODO: wire up to SendPulse / backend
    await new Promise(r => setTimeout(r, 800));
    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-4">
        <div className="text-2xl mb-2">&#x1F389;</div>
        <p className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0]">You're on the list!</p>
        <p className="text-xs text-[#7B7B7B] dark:text-[#848484] mt-1">
          We'll send your survey link shortly. Complete it to unlock 100 MB free storage.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 w-full max-w-sm">
      <Input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        required
        className="flex-1"
      />
      <AccentButton
        variant="primary"
        type="submit"
        disabled={loading}
        className="h-[48px] px-5 text-sm shrink-0"
      >
        {loading ? '...' : 'Notify me'}
      </AccentButton>
    </form>
  );
};

// ─── Main ────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/my" replace />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212] flex flex-col">

      {/* Header */}
      <header className="h-[64px] border-b border-[#ECECEC] dark:border-[#202020] flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center">
          <span className="text-xl font-bold text-[#121212] dark:text-[#D0D0D0]">Clipboarded</span>
          <span className="text-xl font-normal italic text-[#121212] dark:text-[#D0D0D0] ml-1">.me</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeSwitch />
          <Link to="/sign-in">
            <AccentButton variant="secondary" className="h-[36px]">Sign in</AccentButton>
          </Link>
          <Link to="/sign-up">
            <AccentButton variant="primary" className="h-[36px]">Get started</AccentButton>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center">

        {/* Hero */}
        <section className="w-full max-w-3xl px-6 pt-24 pb-16 text-center flex flex-col items-center gap-6">
          <h1 className="text-5xl font-bold text-[#121212] dark:text-[#D0D0D0] leading-tight tracking-tight">
            Stop emailing<br />yourself.
          </h1>
          <p className="text-lg text-[#7B7B7B] dark:text-[#848484] max-w-lg leading-relaxed">
            Copy on your phone, paste on your laptop. Clipboarded keeps your clipboard synced across all your devices — instantly, in real time.
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F1F1F1] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020]">
            <span className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484]">&#x1F4F1; Mobile app coming soon</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/sign-up">
              <AccentButton variant="primary" className="h-[44px] px-6 text-base">Start for free</AccentButton>
            </Link>
          </div>
          <p className="text-xs text-[#7B7B7B] dark:text-[#848484]">Free to start &middot; 50 MB included &middot; No credit card required</p>
        </section>

        {/* Product Preview — real components */}
        <section className="w-full max-w-3xl px-6 pb-20 flex flex-col gap-4">

          {/* Block 1 — Folders */}
          <div className="rounded-3xl bg-[#F7F7F7] dark:bg-[#161616] border border-[#ECECEC] dark:border-[#202020] p-6 flex flex-col gap-4">
            <p className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484] uppercase tracking-wider">Your clipboard folders</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Folder name="Work" date="22/03/2026" retentionPeriod={7776000} />
              <Folder name="Dev snippets" date="21/03/2026" retentionPeriod={604800} />
              <Folder name="Personal" date="13/03/2026" />
            </div>
            <p className="text-xs text-[#7B7B7B] dark:text-[#848484]">
              &#x23F3; Retention badges show when old clips are auto-cleaned — keeping your storage lean automatically.
            </p>
          </div>

          {/* Block 2 — Items inside a folder */}
          <div className="rounded-3xl bg-[#F7F7F7] dark:bg-[#161616] border border-[#ECECEC] dark:border-[#202020] p-6 flex flex-col gap-4">
            <p className="text-xs font-medium text-[#7B7B7B] dark:text-[#848484] uppercase tracking-wider">Inside "Dev snippets"</p>
            <div className="grid grid-cols-2 gap-3">
              <DemoTextItem content={'npm run build:prod'} device="apple" />
              <DemoTextItem content={'https://figma.com/file/abc123...'} device="windows" />
              <DemoImageItem device="android" />
              <DemoTextItem content={'docker compose up -d'} device="unknown" />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-xs text-[#7B7B7B] dark:text-[#848484]">Syncing in real time across your devices</span>
            </div>
          </div>

        </section>

        {/* How it works */}
        <section className="w-full max-w-3xl px-6 pb-20">
          <h2 className="text-2xl font-bold text-[#121212] dark:text-[#D0D0D0] mb-8 text-center">How it works</h2>
          <div className="flex flex-col gap-6 max-w-md mx-auto">
            <Step number="1" title="Copy anything" description="Text, links, images, code snippets — copy it on any device." />
            <Step number="2" title="Paste into a folder" description="Open Clipboarded and paste into any folder. Takes 2 seconds." />
            <Step number="3" title="Access from anywhere" description="Open from any device and it's there. Share a folder with someone and they see it instantly." />
          </div>
        </section>

        {/* Features */}
        <section className="w-full max-w-3xl px-6 pb-20">
          <h2 className="text-2xl font-bold text-[#121212] dark:text-[#D0D0D0] mb-8 text-center">Everything you need</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Feature icon="&#x1F4CB;" title="Clipboard folders" description="Organize your clips into named folders. Keep work, personal, and projects separate." />
            <Feature icon="&#x26A1;" title="Real-time sync" description="Changes appear instantly across all your devices — no refresh, no delay." />
            <Feature icon="&#x1F517;" title="Share with anyone" description="Share a folder link and let others paste directly from your clipboard." />
            <Feature icon="&#x23F3;" title="Auto-cleanup retention" description="Set a retention period per folder — 1 hour to 90 days. Old clips vanish automatically so you never run out of storage. Pro users can enable unlimited retention." />
            <Feature icon="&#x1F5BC;&#xFE0F;" title="Any content type" description="Text, URLs, images, code snippets — Clipboarded handles it all." />
            <Feature icon="&#x1F4BB;" title="Works in your browser" description="Runs in your browser on any device — no install needed. Mobile app coming soon." />
            <Feature icon="&#x1F512;" title="Private by default" description="Your clipboard is yours. Share only what you choose, with who you choose." />
          </div>
        </section>

        {/* Pricing */}
        <section className="w-full max-w-3xl px-6 pb-20">
          <h2 className="text-2xl font-bold text-[#121212] dark:text-[#D0D0D0] mb-2 text-center">Simple pricing</h2>
          <p className="text-sm text-[#7B7B7B] dark:text-[#848484] text-center mb-4">Start free. Complete our short survey and get 100 MB &#x2014; on us.</p>

          {/* Early adopter banner + counter */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#121212] dark:bg-[#D0D0D0]">
              <span className="text-xs font-semibold text-white dark:text-[#121212]">&#x26A1; Early adopter offer &#x2014; first 1,000 users get 50% off forever</span>
            </div>
            <div className="flex flex-col items-center gap-1.5 w-full max-w-xs">
              <div className="w-full h-1.5 bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                <div className="h-full bg-[#121212] dark:bg-[#D0D0D0] rounded-full" style={{ width: '18%' }} />
              </div>
              <p className="text-xs text-[#7B7B7B] dark:text-[#848484]"><span className="font-semibold text-[#121212] dark:text-[#D0D0D0]">820 spots remaining</span> out of 1,000</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* Free */}
            <div className="flex flex-col justify-between gap-4 p-6 rounded-3xl bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020]">
              <div>
                <p className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0]">Free</p>
                <p className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0] mt-1">$0</p>
              </div>
              <ul className="flex flex-col gap-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
                <li>&#x2713; 50 MB storage</li>
                <li>&#x2713; Survey &#x2192; 100 MB bonus</li>
                <li>&#x2713; Unlimited devices</li>
                <li>&#x2713; Real-time sync</li>
                <li>&#x2713; Folder sharing</li>
                <li>&#x2713; 90-day retention per folder</li>
              </ul>
              <Link to="/sign-up">
                <AccentButton variant="secondary" className="w-full h-[40px]">Get started free</AccentButton>
              </Link>
            </div>

            {/* Pro */}
            <div className="flex flex-col justify-between gap-4 p-6 rounded-3xl bg-[#121212] dark:bg-[#D0D0D0] border border-[#121212] dark:border-[#D0D0D0]">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-white dark:text-[#121212]">Pro</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 dark:bg-black/10 text-white dark:text-[#121212] font-medium">Popular</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-white dark:text-[#121212]">$2.50</p>
                  <p className="text-sm text-white/60 dark:text-[#121212]/60 line-through">$5</p>
                </div>
                <p className="text-xs text-white/60 dark:text-[#121212]/60 mt-0.5">per month &#x2014; 5 GB storage</p>
              </div>
              <ul className="flex flex-col gap-2 text-sm text-white dark:text-[#121212] opacity-80">
                <li>&#x2713; 5 GB storage</li>
                <li>&#x2713; Unlimited folders</li>
                <li>&#x2713; <strong>Unlimited</strong> retention</li>
                <li>&#x2713; Mobile app (iOS &amp; Android)</li>
                <li>&#x2713; Priority sync</li>
                <li>&#x2713; 50% off forever</li>
              </ul>
              <a href="#waitlist">
                <AccentButton variant="secondary" className="w-full h-[40px]">Join waitlist</AccentButton>
              </a>
            </div>

            {/* Power */}
            <div className="flex flex-col justify-between gap-4 p-6 rounded-3xl bg-[#FCFCFC] dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020]">
              <div>
                <p className="text-sm font-semibold text-[#121212] dark:text-[#D0D0D0]">Power</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">$4</p>
                  <p className="text-sm text-[#7B7B7B] line-through">$8</p>
                </div>
                <p className="text-xs text-[#7B7B7B] dark:text-[#848484] mt-0.5">per month &#x2014; 10 GB storage</p>
              </div>
              <ul className="flex flex-col gap-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
                <li>&#x2713; 10 GB storage</li>
                <li>&#x2713; Unlimited folders</li>
                <li>&#x2713; <strong>Unlimited</strong> retention</li>
                <li>&#x2713; Mobile app (iOS &amp; Android)</li>
                <li>&#x2713; Priority sync</li>
                <li>&#x2713; 50% off forever</li>
              </ul>
              <a href="#waitlist">
                <AccentButton variant="secondary" className="w-full h-[40px]">Join waitlist</AccentButton>
              </a>
            </div>

          </div>
          <p className="text-xs text-[#7B7B7B] dark:text-[#848484] text-center mt-4">Early adopter pricing locks in for life &#x2014; no surprises when we launch.</p>
        </section>

        {/* Waitlist / Mobile CTA */}
        <section id="waitlist" className="w-full max-w-3xl px-6 pb-24">
          <div className="rounded-3xl bg-[#F7F7F7] dark:bg-[#161616] border border-[#ECECEC] dark:border-[#202020] p-10 flex flex-col items-center gap-4 text-center">
            <div className="text-3xl">&#x1F4F1;</div>
            <h2 className="text-2xl font-bold text-[#121212] dark:text-[#D0D0D0]">Mobile app coming soon</h2>
            <p className="text-sm text-[#7B7B7B] dark:text-[#848484] max-w-sm">
              iOS and Android apps are in development. Join the waitlist &#x2014; we'll notify you when they launch and send you a short survey to help shape the product.
            </p>
            <p className="text-xs font-semibold text-[#121212] dark:text-[#D0D0D0]">Complete the survey &#x2192; get 100 MB free storage</p>
            <WaitlistForm />
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="border-t border-[#ECECEC] dark:border-[#202020] px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center">
          <span className="text-sm font-bold text-[#121212] dark:text-[#D0D0D0]">Clipboarded</span>
          <span className="text-sm font-normal italic text-[#121212] dark:text-[#D0D0D0] ml-1">.me</span>
        </div>
        <p className="text-xs text-[#7B7B7B] dark:text-[#848484]">&copy; {new Date().getFullYear()} Clipboarded.me</p>
        <div className="flex gap-4">
          <Link to="/sign-in" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Sign in</Link>
          <Link to="/sign-up" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Sign up</Link>
          <a href="mailto:hello@clipboarded.me" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Contact</a>
          <Link to="/privacy" className="text-xs text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] transition-colors">Privacy</Link>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
